/// <reference path="../../ui/types.d.ts" />
import { FC, useRef, useEffect, useState } from 'react';

interface LoginField {
    key?: string;
    label?: string;
    labelHtml?: string;
    helpText?: string;
    helpTextHtml?: string;
    type?: string;
    defaultValue?: any;
    dependencySettings?: string;
    isMulti?: boolean;
    isSearchable?: boolean;
    options?: Array<{ value: string; label: string }>;
    noticeType?: string;
    noIcon?: boolean;
    accept?: string;
}

interface LoginPageProps {
    name: string;
    loginFields: LoginField[];
    oauthLogin?: boolean;
    oauthUrl?: string;
    oauthMode?: string;
}

const renderField = (field: LoginField, readFileData: (e: any) => void, uploadFiles: (key: string) => void) => {
    if (!field.key) {
        // Label-only field
        return field.labelHtml ? (
            <crowdin-p
                {...(field.dependencySettings ? { 'data-dependency': field.dependencySettings } : {})}
                dangerouslySetInnerHTML={{ __html: field.labelHtml }}
            />
        ) : (
            <crowdin-p {...(field.dependencySettings ? { 'data-dependency': field.dependencySettings } : {})}>
                {field.label}
            </crowdin-p>
        );
    }

    const commonProps = {
        id: field.key,
        label: field.label,
        ...(field.helpText ? { 'help-text': field.helpText } : {}),
        ...(field.helpTextHtml ? { 'help-text-html': field.helpTextHtml } : {}),
        ...(field.dependencySettings ? { 'data-dependency': field.dependencySettings } : {}),
    };

    // Special field types
    if (['checkbox', 'select', 'textarea', 'file', 'notice'].includes(field.type || '')) {
        switch (field.type) {
            case 'checkbox':
                return (
                    <crowdin-checkbox
                        {...commonProps}
                        value="false"
                        use-switch
                        {...(field.defaultValue === true ? { checked: String(field.defaultValue) } : {})}
                    />
                );

            case 'select':
                return (
                    <crowdin-select
                        {...commonProps}
                        {...(field.isMulti ? { 'is-multi': true, 'close-on-select': 'false' } : {})}
                        {...(field.isSearchable ? { 'is-searchable': true } : {})}
                    >
                        {field.options?.map((option) => (
                            <option
                                value={option.value}
                                {...(field.defaultValue === option.value ? { selected: true } : {})}
                            >
                                {option.label}
                            </option>
                        ))}
                    </crowdin-select>
                );

            case 'textarea':
                return <crowdin-textarea {...commonProps} value={field.defaultValue || ''} />;

            case 'notice':
                return (
                    <crowdin-alert
                        {...(field.noticeType ? { type: field.noticeType } : {})}
                        {...(field.label ? { title: field.label } : {})}
                        {...(field.noIcon ? { 'no-icon': String(field.noIcon) } : {})}
                        {...(field.dependencySettings ? { 'data-dependency': field.dependencySettings } : {})}
                        dangerouslySetInnerHTML={{ __html: field.helpTextHtml || field.helpText || '' }}
                    />
                );

            case 'file':
                return (
                    <div className="file-field">
                        {field.helpText && <div className="help-text">{field.helpText}</div>}
                        {field.helpTextHtml && <div className="help-text">{field.helpTextHtml}</div>}
                        <div className="upload">
                            <crowdin-button outlined onClick={() => uploadFiles(field.key!)}>
                                {field.label}
                            </crowdin-button>
                            <textarea hidden id={field.key}></textarea>
                            <input
                                id={`store_${field.key}`}
                                data-id={field.key}
                                {...(field.accept ? { accept: field.accept } : {})}
                                onChange={(e) => readFileData(e)}
                                hidden
                                type={field.type}
                            />
                            <div className="uploaded-file">
                                <i>No file</i>
                            </div>
                        </div>
                    </div>
                );
        }
    }

    // Default input field
    return (
        <crowdin-input
            {...commonProps}
            {...(field.type ? { type: field.type } : {})}
            value={field.defaultValue || ''}
        />
    );
};

export const LoginPage: FC<LoginPageProps> = ({ name, loginFields, oauthLogin, oauthUrl, oauthMode }) => {
    const refreshIntervalId = useRef<any>(null);

    const [isLoading, setIsLoading] = useState(false);

    // Cleanup interval on component unmount
    useEffect(() => {
        // Initialize dependencies after component is mounted
        setTimeout(() => {
            if (window.initializeDependencies) {
                window.initializeDependencies();
            }
        }, 100);

        return () => {
            if (refreshIntervalId.current) {
                clearInterval(refreshIntervalId.current);
                refreshIntervalId.current = null;
            }
        };
    }, []);

    function oauthLoginHandler() {
        const url = oauthUrl;
        if (url) {
            openOAuthPopup(url);
            return;
        }

        setIsLoading(true);

        checkOrigin()
            .then((queryParams) =>
                fetch(`api/oauth-url${queryParams}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ loginForm: getLoginForm() }),
                }),
            )
            .then(checkResponse)
            .then((res) => openOAuthPopup(res.url))
            .catch((e) => {
                setIsLoading(false);
                catchRejection(e, "Can't get oauth token");
            });
    }

    function openOAuthPopup(url: string) {
        window.open(url, name, 'location=0,status=0,width=800,height=400');

        if (oauthMode === 'polling') {
            if (refreshIntervalId.current) {
                clearInterval(refreshIntervalId.current);
            }

            refreshIntervalId.current = setInterval(
                () =>
                    checkOrigin()
                        .then((queryParams) => fetch(`api/oauth-polling${queryParams}`, { method: 'POST' }))
                        .then(checkResponse)
                        .then((res) => {
                            if (res.oauthCreds) {
                                integrationLogin({
                                    refreshToken: res.oauthCreds.refreshToken,
                                    accessToken: res.oauthCreds.accessToken,
                                    expireIn: res.oauthCreds.expireIn,
                                    timestamp: res.oauthCreds.timestamp,
                                });
                                if (refreshIntervalId.current) {
                                    clearInterval(refreshIntervalId.current);
                                }
                            }
                        }),
                2000,
            );
            return;
        }

        postPromises['oauth_popup'] = {
            resolve: (data: any) => {
                if (data.error) {
                    showToast(data.error);
                    return;
                }
                integrationLogin({
                    refreshToken: data.refreshToken,
                    accessToken: data.accessToken,
                    expireIn: data.expireIn,
                    timestamp: data.timestamp,
                });
            },
        };
    }

    function integrationLogin(oauthCredentials?: any) {
        const credentials = oauthCredentials || getLoginForm();

        setIsLoading(true);

        checkOrigin()
            .then((queryParams) =>
                fetch(`api/login${queryParams}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ credentials }),
                }),
            )
            .then(checkResponse)
            .then(() => localStorage.removeItem(`revised_${name}`))
            .then(reloadLocation)
            .catch((e) => {
                const errorMessage = e.error?.message || e.message || 'Credentials are not stored';

                setIsLoading(false);

                catchRejection(e, errorMessage);
            });
    }

    function getLoginForm() {
        return loginFields
            .filter((f) => f.key)
            .reduce((acc, f) => {
                if (f.type === 'checkbox') {
                    const checkbox = document.querySelector(`#${f.key}`) as HTMLInputElement;
                    acc[f.key!] = !!checkbox.checked;
                } else {
                    acc[f.key!] =
                        document.querySelector(`#${f.key}`)?.getAttribute('value') ||
                        (document.querySelector(`#${f.key}`) as any)?.value;
                }
                return acc;
            }, {} as any);
    }

    function uploadFiles(id: string) {
        const input = document.querySelector(`#store_${id}`) as HTMLInputElement;
        input.value = '';
        input.click();
    }

    function readFileData(event: any) {
        const reader = new FileReader();
        const identifier = event.target.getAttribute('data-id');
        const fileName = document.querySelector('.uploaded-file') as HTMLDivElement;
        const input = document.querySelector(`#${identifier}`);
        const file = event.target.files[0];
        if (fileName) {
            fileName.innerText = file.name;
        }

        reader.onloadstart = () => setIsLoading(true);

        reader.onloadend = () => setIsLoading(false);

        reader.onload = () => {
            if (input) {
                // @ts-ignore
                input.value = reader.result;
            }
        };

        reader.readAsText(file);
    }

    return (
        <div>
            <div className="i_w center">
                <div>
                    <crowdin-card is-shadowed is-padding-lg class="login">
                        <img alt={`${name} logo`} src="logo.png" />
                        <crowdin-h4 id="integration-name">{name}</crowdin-h4>
                        <div className="inputs">
                            {loginFields.map((field) => (
                                <>
                                    {renderField(field, readFileData, uploadFiles)}

                                    <div
                                        style={{ padding: '8px' }}
                                        {...(field.dependencySettings
                                            ? { 'data-dependency': field.dependencySettings }
                                            : {})}
                                    ></div>
                                </>
                            ))}
                        </div>
                        <crowdin-button
                            id="login-button"
                            disabled={isLoading}
                            is-loading={isLoading}
                            outlined
                            icon-after="arrow_forward"
                            onClick={oauthLogin ? () => oauthLoginHandler() : () => integrationLogin()}
                        >
                            Log In With {name}
                        </crowdin-button>
                    </crowdin-card>
                </div>
            </div>
            <crowdin-toasts></crowdin-toasts>
        </div>
    );
};
